import { supabase } from "@/integrations/supabase/client";
import {
  isDuplicateProposeError,
  isVoiceRateLimitError,
  VOICE_WRITE_ALREADY_SAVED_RESULT,
  VOICE_WRITE_RATE_LIMITED_RESULT,
} from "@/lib/voiceWriteBudget";

/** What the write did. `duplicate` means this proposal had already been saved. */
export type SaveVoiceTodoAction = "insert" | "duplicate" | "rejected_rate_limit";

/**
 * Which list it landed on, and why. Kin has to SAY this, and the four cases
 * need four different sentences — `fell_back` in particular must never be
 * silent, or she thinks the item is on a list that does not exist.
 */
export type VoiceTodoListOutcome =
  | "default"
  | "matched"
  | "created"
  | "fell_back"
  | "existing";

export interface SaveVoiceTodoResult {
  ok: boolean;
  item?: string;
  listName?: string | null;
  listOutcome?: VoiceTodoListOutcome;
  action?: SaveVoiceTodoAction;
  /** Set only when ok is false. Already phrased for Kin to speak. */
  error?: string;
  /** Set only when ok is TRUE and the model should know something anyway. */
  note?: string;
}

/**
 * Voice's second write destination (issue #1159) — a real to-do, not a
 * Ready-to-Fire card.
 *
 * Everything goes through the `save_voice_todo` RPC. That is deliberate and it
 * is the whole reason this needed SQL:
 *
 *   - `todo_list_items` RLS is `for ALL using (auth.uid() = user_id)`, so this
 *     file COULD insert directly. It must not. The voice write cap
 *     (VOICE_WRITE_CAP, 12 per 10 min) lives in database triggers, not in
 *     TypeScript, so a direct insert path would be uncapped — and nothing
 *     would ever report that it was.
 *   - Resolving-or-creating a list and inserting the item have to be ONE
 *     transaction. A client-side two-step that fails on the second half leaves
 *     an empty list in her app that she never asked for.
 *
 * ⚠️ NO LEGACY FALLBACK, unlike captureReminder.
 *
 * captureReminder keeps a pre-#933 two-insert path for the window between the
 * frontend merging and Kasandra running the SQL, because that path is what
 * production actually supported before. Here there IS no previous behavior to
 * fall back to — voice has never written a to-do — and the only "fallback"
 * available would be a direct insert that skips the cap and can orphan a list.
 * Shipping that to save a few hours of migration lag would be building the
 * exact hole this migration exists to close. So if the RPC is not there yet,
 * this says so honestly and writes nothing.
 */
export async function saveVoiceTodo(
  text: string,
  options?: {
    listName?: string | null;
    createList?: boolean;
    source?: string;
    proposeId?: string | null;
  },
): Promise<SaveVoiceTodoResult> {
  const item = String(text ?? "").trim().replace(/[\s.,!?;:]+$/, "").trim();
  if (!item) return { ok: false, error: "Nothing to save" };

  const listName = options?.listName?.trim() || null;
  const createList = options?.createList === true;
  const source = options?.source ?? "voice";
  const proposeId = options?.proposeId ?? null;

  const { data, error } = await (supabase as any).rpc("save_voice_todo", {
    p_item: item,
    p_list_name: listName,
    p_create_list: createList,
    p_source: source,
    ...(proposeId ? { p_propose_id: proposeId } : {}),
  });

  if (error) {
    // PGRST202 = the function does not exist yet. The frontend ships on merge
    // and Kasandra runs the SQL by hand, so this window is real — and the
    // honest answer is that the capability is not live, not a silent downgrade
    // to an uncapped insert.
    if (String((error as { code?: string })?.code ?? "") === "PGRST202") {
      console.error("[saveVoiceTodo] save_voice_todo RPC missing — migration not run yet");
      return {
        ok: false,
        error: "Can't add to her to-do lists just yet — offer to save it as a card instead",
      };
    }

    // ⚠️ The race the unique index exists for. Two truly simultaneous calls can
    // both pass the propose lookup inside the function; one then loses on the
    // index and its transaction aborts. That is a SUCCESS — the other call
    // wrote the row — and must not be reported as a failure the model retries.
    if (isDuplicateProposeError(error)) {
      return {
        ok: true,
        item,
        action: "duplicate",
        listOutcome: "existing",
        note: VOICE_WRITE_ALREADY_SAVED_RESULT,
      };
    }
    if (isVoiceRateLimitError(error)) {
      return { ok: false, action: "rejected_rate_limit", error: VOICE_WRITE_RATE_LIMITED_RESULT };
    }
    console.error("[saveVoiceTodo] rpc save_voice_todo failed:", error);
    return { ok: false, error: "Couldn't save that" };
  }

  const row = Array.isArray(data) ? data[0] : data;
  const action = row?.action as string | undefined;

  // A call that resolved without error is not evidence a row was written —
  // house rule 16. #537's fix created #545 by assuming the happy path here.
  if (!action) {
    console.error("[saveVoiceTodo] rpc returned no action row:", data);
    return { ok: false, error: "Couldn't save that" };
  }

  const listOutcome = (row?.list_outcome as VoiceTodoListOutcome | undefined) ?? undefined;
  const resolvedList = (row?.list_name as string | null | undefined) ?? null;

  if (action === "duplicate") {
    return {
      ok: true,
      item,
      listName: resolvedList,
      listOutcome: listOutcome ?? "existing",
      action: "duplicate",
      note: VOICE_WRITE_ALREADY_SAVED_RESULT,
    };
  }
  if (action === "rejected_rate_limit") {
    return { ok: false, action: "rejected_rate_limit", error: VOICE_WRITE_RATE_LIMITED_RESULT };
  }
  if (action !== "insert") {
    return { ok: false, error: "Couldn't save that" };
  }

  return {
    ok: true,
    item,
    listName: resolvedList,
    listOutcome: listOutcome ?? "default",
    action: "insert",
  };
}
